/**
 * usePage — the page data hook extracted from the Phase-1 Roster pattern (DRY before 12 pages).
 * Owns loading / error / data / meta over fetchEnvelope. On error it surfaces ONLY the RFC 9457
 * problem `title` (never a stack/SQL/PHI string); response rows are never logged. Pass a null path
 * to skip the fetch (e.g. a member-keyed screen with no member selected yet).
 *
 * Recovery: a transient error is NOT terminal. An internal attempt counter is a fetch dependency, and
 * retry() bumps it (re-running the effect + clearing the error) so a static-path page can recover from a
 * blip WITHOUT a full-page F5. The returned PageState keeps its original fields (data/meta/loading/error)
 * and ADDS retry (backward-compatible for every consumer).
 */
import { useCallback, useEffect, useState } from "react";
import type { z } from "zod";
import type { Meta } from "@shared/contracts";
import { fetchEnvelope, ApiProblem, ApiShapeError } from "./api";

export interface PageState<T> {
  data: T | null;
  meta: Meta | undefined;
  loading: boolean;
  error: string | null;
  /** Re-run the fetch and clear the current error (fail-forward recovery, no page reload). */
  retry: () => void;
}

export function usePage<T>(path: string | null, schema?: z.ZodType<T>): PageState<T> {
  const [data, setData] = useState<T | null>(null);
  const [meta, setMeta] = useState<Meta | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // attempt is a fetch DEP: bumping it (via retry) re-runs the effect so a transient error is recoverable
  // in place. It starts at 0 and only ever increments; the value itself is not surfaced.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    // H2: RESET the previous member's data/meta BEFORE fetching. Without this, Member360 + every
    // member-keyed picker page renders the PREVIOUS member's clinical rows/badge during navigation
    // (a stale clinical mis-attribution). Clear first, then load.
    setData(null);
    setMeta(undefined);
    setLoading(true);
    setError(null);
    // schema (when passed) is a module-level constant, so it is intentionally NOT a fetch dependency:
    // adding it would risk a re-fetch loop for no benefit (its identity is stable). path/attempt remain
    // the only triggers, preserving the existing member-reset + cancellation behavior.
    fetchEnvelope<T>(path, undefined, schema)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setMeta(r.meta);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Surface only a sanitized title — no PHI, no stack. A shape-drift (ApiShapeError) routes to the
        // SAME recovery UI via its stable title instead of crashing the render.
        setError(
          e instanceof ApiProblem
            ? e.problem.title
            : e instanceof ApiShapeError
              ? e.title
              : "Failed to load.",
        );
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return { data, meta, loading, error, retry };
}
